/**
 * PO Lines Filters Panel Component
 * CR-3413: Updated filters - removed Delivery From/To, combined status filters,
 * added searchable dropdowns for Supplier and Customer/Site
 */
import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Button } from '../../../ui/button';
import { Search, Calendar, X, ChevronDown } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import { POLinesFilters, FilterOptions, STATUS_OPTIONS } from './types';
import { useAuth } from '../../../../contexts/AuthContext';
import { useView } from '../../../../contexts/ViewContext';
import { useMenuSettings } from '../../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../../hooks/usePortalMode';

interface POLinesFiltersPanelProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  onHideFilters?: () => void;
}

export function POLinesFiltersPanel({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  onHideFilters,
}: POLinesFiltersPanelProps) {
  // CR-3413: State for searchable dropdowns
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);

  // CR-4716: Supplier users cannot see commercial/internal filters
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';

  // When schedule_collaboration is enabled, hide collaboration-related filters
  // (managed at the schedule level instead).
  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');

  // CR-4577: Filter customers by view context (group/division/site)
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  const { isSupplier } = usePortalMode();
  const viewScopedCustomers = useMemo(() => {
    const all = filterOptions?.customers || [];
    // CR-5307: in supplier-portal mode the customer list is already scoped
    // by the backend (filter-options narrows to customers the supplier
    // serves). selectedCompanyId is the SUPPLIER's id, never matches a
    // customer id, so re-applying the customer-side narrowing below would
    // collapse the list to empty and leave the Company Site dropdown blank.
    if (isSupplier) return all;
    if (viewLevel === 'SITE' && selectedCompanyId) return all.filter(c => c.id === selectedCompanyId);
    if (viewLevel === 'COMPANY' && selectedCompanyId) return all.filter(c => c.id === selectedCompanyId);
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = all.filter(c => c.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : all;
    }
    return all;
  }, [filterOptions?.customers, viewLevel, selectedCompanyId, selectedGroupId, isSupplier]);

  // CR-3413: Filtered suppliers based on search
  const filteredSuppliers = useMemo(() => {
    const searchTerm = (filters.supplierSearch || '').toLowerCase();
    if (!searchTerm) return filterOptions?.suppliers || [];
    return (filterOptions?.suppliers || []).filter(s =>
      s.name.toLowerCase().includes(searchTerm)
    );
  }, [filterOptions?.suppliers, filters.supplierSearch]);

  // CR-3413: Filtered customers/sites based on search (scoped by view context)
  const filteredCustomers = useMemo(() => {
    const searchTerm = (filters.customerSearch || '').toLowerCase();
    if (!searchTerm) return viewScopedCustomers;
    return viewScopedCustomers.filter(c =>
      c.name.toLowerCase().includes(searchTerm)
    );
  }, [viewScopedCustomers, filters.customerSearch]);

  // CR-3413: Get display name for selected supplier
  const selectedSupplierName = useMemo(() => {
    if (!filters.supplier) return '';
    const supplier = (filterOptions?.suppliers || []).find(s => s.id.toString() === filters.supplier);
    return supplier?.name || '';
  }, [filters.supplier, filterOptions?.suppliers]);

  // CR-3413: Get display name for selected customer/site
  const selectedCustomerName = useMemo(() => {
    if (!filters.customer) return '';
    const customer = (filterOptions?.customers || []).find(c => c.id.toString() === filters.customer);
    return customer?.name || '';
  }, [filters.customer, filterOptions?.customers]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Filter PO Lines</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); onSearch(); }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* PO Number */}
          <div>
            <label className="text-sm font-medium mb-2 block">PO Number</label>
            <input
              type="text"
              placeholder="620596..."
              value={filters.poNumber}
              onChange={(e) => onFilterChange('poNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Line Number */}
          <div>
            <label className="text-sm font-medium mb-2 block">Line #</label>
            <input
              type="text"
              placeholder="1, 2, 3..."
              value={filters.lineNumber}
              onChange={(e) => onFilterChange('lineNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* CR-3413: Searchable Supplier Dropdown */}
          <div className="relative">
            <label className="text-sm font-medium mb-2 block">Supplier Site</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search supplier sites..."
                value={filters.supplier ? selectedSupplierName : filters.supplierSearch}
                onChange={(e) => {
                  onFilterChange('supplierSearch', e.target.value);
                  onFilterChange('supplier', '');
                  setSupplierDropdownOpen(true);
                }}
                onFocus={() => setSupplierDropdownOpen(true)}
                className="w-full px-3 py-2 pr-8 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              {filters.supplier && (
                <button
                  onClick={() => {
                    onFilterChange('supplier', '');
                    onFilterChange('supplierSearch', '');
                  }}
                  className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {supplierDropdownOpen && filteredSuppliers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                {filteredSuppliers.slice(0, 50).map(s => (
                  <button
                    key={s.id}
                    onClick={() => {
                      onFilterChange('supplier', s.id.toString());
                      onFilterChange('supplierSearch', '');
                      setSupplierDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                  >
                    {s.name}
                  </button>
                ))}
              </div>
            )}
            {supplierDropdownOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setSupplierDropdownOpen(false)}
              />
            )}
          </div>

          {/* Supplier Group */}
          <div>
            <label className="text-sm font-medium mb-2 block">Supplier Group</label>
            <SearchableSelect
              options={(filterOptions?.supplier_groups || []).map((g) => ({ value: g.name, label: g.name }))}
              value={filters.supplierGroup}
              onChange={(val) => onFilterChange('supplierGroup', val)}
              placeholder="All Supplier Groups"
            />
          </div>

          {/* CR-3413: Searchable Site Dropdown (Customer for customer portal, Site for supplier portal) */}
          <div className="relative">
            <label className="text-sm font-medium mb-2 block">Company Site</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search company sites..."
                value={filters.customer ? selectedCustomerName : filters.customerSearch}
                onChange={(e) => {
                  onFilterChange('customerSearch', e.target.value);
                  onFilterChange('customer', '');
                  setCustomerDropdownOpen(true);
                }}
                onFocus={() => setCustomerDropdownOpen(true)}
                className="w-full px-3 py-2 pr-8 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              {filters.customer && (
                <button
                  onClick={() => {
                    onFilterChange('customer', '');
                    onFilterChange('customerSearch', '');
                  }}
                  className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {customerDropdownOpen && filteredCustomers.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                {filteredCustomers.slice(0, 50).map(c => (
                  <button
                    key={c.id}
                    onClick={() => {
                      onFilterChange('customer', c.id.toString());
                      onFilterChange('customerSearch', '');
                      setCustomerDropdownOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            )}
            {customerDropdownOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setCustomerDropdownOpen(false)}
              />
            )}
          </div>

          {/* Commodity Group Dropdown */}
          <div>
            <label className="text-sm font-medium mb-2 block">Commodity Group</label>
            <select
              value={filters.commodityGroup}
              onChange={(e) => {
                onFilterChange('commodityGroup', e.target.value);
                if (e.target.value !== filters.commodityGroup) {
                  onFilterChange('commodityType', '');
                }
              }}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Commodity Groups</option>
              {(filterOptions?.commodity_groups || []).map(cg => (
                <option key={cg.id} value={cg.id}>{cg.name}</option>
              ))}
            </select>
          </div>

          {/* Commodity Type Dropdown */}
          <div>
            <label className="text-sm font-medium mb-2 block">Commodity Type</label>
            <select
              value={filters.commodityType}
              onChange={(e) => onFilterChange('commodityType', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Commodity Types</option>
              {(filterOptions?.commodity_types || [])
                .filter(ct => !filters.commodityGroup || ct.commodity_group_id === parseInt(filters.commodityGroup))
                .map(ct => (
                  <option key={ct.id} value={ct.id}>{ct.name}</option>
                ))}
            </select>
          </div>

          {/* Part Number */}
          <div>
            <label className="text-sm font-medium mb-2 block">Part Number</label>
            <input
              type="text"
              placeholder="Part number..."
              value={filters.partNumber}
              onChange={(e) => onFilterChange('partNumber', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium mb-2 block">Description</label>
            <input
              type="text"
              placeholder="Description..."
              value={filters.description}
              onChange={(e) => onFilterChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* CR-3413: Unified Status filter - combines previous Status and Ack Status */}
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <select
              value={filters.status}
              onChange={(e) => onFilterChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* On Hold */}
          {!scheduleCollabEnabled && (
            <div>
              <label className="text-sm font-medium mb-2 block">On Hold</label>
              <select
                value={filters.on_hold}
                onChange={(e) => onFilterChange('on_hold', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All</option>
                <option value="yes">On Hold</option>
                <option value="no">Not On Hold</option>
              </select>
            </div>
          )}

          {/* Buyer ID — CR-4716: hidden from supplier users */}
          {!isSupplierUser && (
            <div>
              <label className="text-sm font-medium mb-2 block">Buyer ID</label>
              <input
                type="text"
                placeholder="Buyer ID..."
                value={filters.buyerId}
                onChange={(e) => onFilterChange('buyerId', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          {/* Batch/Serial Number */}
          <div>
            <label className="text-sm font-medium mb-2 block">Batch/Serial No</label>
            <input
              type="text"
              placeholder="Batch or serial..."
              value={filters.batchSerialNo}
              onChange={(e) => onFilterChange('batchSerialNo', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* OTIF Overridden — CR-4716: hidden from supplier users */}
          {!isSupplierUser && (
            <div>
              <label className="text-sm font-medium mb-2 block">OTIF Overridden</label>
              <select
                value={filters.otifOverridden}
                onChange={(e) => onFilterChange('otifOverridden', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All</option>
                <option value="yes">Overridden</option>
                <option value="no">Not Overridden</option>
              </select>
            </div>
          )}

          {/* Next Action */}
          <div>
            <label className="text-sm font-medium mb-2 block">Next Action</label>
            <select
              value={filters.nextAction}
              onChange={(e) => onFilterChange('nextAction', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All</option>
              <option value="customer">Waiting Customer</option>
              <option value="supplier">Waiting Supplier</option>
            </select>
          </div>

          {/* CR-3413: Removed Delivery From/To filters - not required */}

          {/* Original Delivery Date From */}
          <div>
            <label className="text-sm font-medium mb-2 block">ODD From</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="date"
                value={filters.oddFrom}
                onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Original Delivery Date To */}
          <div>
            <label className="text-sm font-medium mb-2 block">ODD To</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                type="date"
                value={filters.oddTo}
                onChange={(e) => onFilterChange('oddTo', e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* PPV Filter — CR-4716: hidden from supplier users */}
          {!isSupplierUser && (
            <div>
              <label className="text-sm font-medium mb-2 block">PPV</label>
              <select
                value={filters.ppv}
                onChange={(e) => onFilterChange('ppv', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">All</option>
                <option value="positive">Positive (Cost Overrun)</option>
                <option value="negative">Negative (Savings)</option>
                <option value="zero">Zero (No Variance)</option>
              </select>
            </div>
          )}
        </div>

        {/* Search + Clear + Close Buttons */}
        <div className="flex gap-3 mt-4 pt-4 border-t">
          <Button type="submit" className="px-6">
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
          <Button variant="outline" onClick={onClearFilters} className="px-6">
            <X className="h-4 w-4 mr-2" />
            Clear All
          </Button>
          {onHideFilters && (
            <Button variant="outline" size="sm" onClick={onHideFilters}>
              Hide Filters
            </Button>
          )}
        </div>
        </form>
      </CardContent>
    </Card>
  );
}
